import { useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../../store/useAuthStore';
import { useMyStats } from '../../hooks/useLeaderboard';
import brandMark from '../../assets/brand/cyberelites-mark.png';

/**
 * FortressShell — the application chrome for trainees whose org runs the
 * 'fortress' (Citadel) template. Instead of the default SaaS sidebar, it wraps
 * pages in the Citadel's dark command bar and re-themes the whole content
 * subtree to the Citadel palette — so opening a lab or course NEVER drops the
 * trainee into the plain default UI. The `/trainee` route paints its full-bleed
 * 3D Citadel over this shell; every other trainee page (labs, courses, console)
 * renders its real content inside this dark fortress chrome.
 *
 * Theming trick: `data-mantine-color-scheme="dark"` on the wrapper flips the
 * whole subtree to the app's dark tokens (the token CSS matches nested
 * subtrees, not just :root), and the inline `--c-*` overrides retint those dark
 * tokens to the Citadel navy+green — no global theme state is touched.
 */

const FORTRESS_CSS = `
.fortress-shell{min-height:100vh;background:rgb(var(--c-void));color:rgb(var(--c-txt));display:flex;flex-direction:column;font-family:'Manrope',ui-sans-serif,system-ui,sans-serif}
.fortress-shell .fs-bar{position:sticky;top:0;z-index:40;display:flex;align-items:center;gap:22px;height:60px;padding:0 clamp(16px,4vw,40px);background:rgba(7,11,18,.86);backdrop-filter:blur(14px);border-bottom:1px solid rgba(255,255,255,.07)}
.fortress-shell .fs-brand{display:flex;align-items:center;gap:11px;text-decoration:none;color:inherit}
.fortress-shell .fs-brand img{width:30px;height:30px;object-fit:contain}
.fortress-shell .fs-brand b{font-family:'Barlow Condensed','Arial Narrow',sans-serif;font-weight:700;font-size:20px;letter-spacing:.02em;line-height:1;color:#e9eef4}
.fortress-shell .fs-brand b i{font-style:normal;color:#3ccb84}
.fortress-shell .fs-brand small{display:block;font-size:9.5px;letter-spacing:.16em;text-transform:uppercase;color:#7d90a6;margin-top:3px;font-family:'IBM Plex Mono',monospace}
.fortress-shell .fs-nav{display:flex;gap:4px;margin-left:8px}
.fortress-shell .fs-nav a{font-family:'Barlow Condensed','Arial Narrow',sans-serif;font-weight:600;font-size:15px;letter-spacing:.08em;text-transform:uppercase;color:#8aa0b5;text-decoration:none;padding:8px 13px;border-radius:9px;transition:color .15s,background .15s}
.fortress-shell .fs-nav a:hover{color:#e9eef4;background:rgba(255,255,255,.05)}
.fortress-shell .fs-nav a.on{color:#e9eef4;background:rgba(60,203,132,.14)}
.fortress-shell .fs-right{margin-left:auto;display:flex;align-items:center;gap:12px}
.fortress-shell .fs-pts{font-family:'IBM Plex Mono',monospace;font-size:12.5px;font-weight:600;color:#d9bd6e;border:1px solid rgba(217,189,110,.32);border-radius:999px;padding:5px 12px;white-space:nowrap}
.fortress-shell .fs-pts small{color:#8aa0b5;font-weight:500}
.fortress-shell .fs-av{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;color:#070b12;background:#3ccb84;text-decoration:none;border:none}
.fortress-shell .fs-main{flex:1;min-height:0;overflow-y:auto}
@media (max-width:640px){.fortress-shell .fs-brand small{display:none}.fortress-shell .fs-nav a{padding:8px 9px;font-size:13.5px}}
`;

const initials = (s: string) =>
  (s || 'OP').split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'OP';

// Retint the app's dark tokens to the Citadel navy+green for the whole subtree.
const CITADEL_TOKENS: Record<string, string> = {
  '--c-void': '7 11 18',
  '--c-deep': '16 26 39',
  '--c-panel': '13 21 32',
  '--c-card': '16 26 39',
  '--c-card-hover': '20 32 47',
  '--c-elevated': '20 32 47',
  '--c-neon': '60 203 132',
  '--c-neon-bright': '124 231 172',
  '--c-txt': '233 238 244',
  '--c-txt-bright': '255 255 255',
  '--c-bd': '#1b2735',
  '--c-bd-bright': '#2a3a4c',
  '--c-bd-subtle': '#14202c',
};

export function FortressShell({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const user = useAuthStore((s) => s.user);
  const stats = useMyStats().data;

  // Citadel fonts (same as the 3D console) so the command bar matches.
  useEffect(() => {
    const id = 'citadel-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Manrope:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const emailName = user?.email ? (user.email.split('@')[0] ?? '') : '';
  const rawName = (stats?.name ?? '').trim();
  const name = emailName && (!rawName || /^user[\s_-]?\d+$/i.test(rawName)) ? emailName : (rawName || emailName || 'operator');
  const pts = stats?.points ?? 0;

  const isOn = (p: string) => (p === '/trainee' ? pathname === '/trainee' : pathname.startsWith(p));

  return (
    <div className="fortress-shell" data-mantine-color-scheme="dark" style={CITADEL_TOKENS as React.CSSProperties}>
      <style>{FORTRESS_CSS}</style>
      <header className="fs-bar">
        <Link to="/trainee" className="fs-brand" aria-label="The Citadel home">
          <img src={brandMark} alt="" />
          <span><b>CYBER<i>ELITES</i></b><small>The Citadel</small></span>
        </Link>
        <nav className="fs-nav" aria-label="Sections">
          <Link to="/trainee" className={isOn('/trainee') ? 'on' : ''}>Dashboard</Link>
          <Link to="/courses" className={isOn('/courses') ? 'on' : ''}>Courses</Link>
          <Link to="/labs" className={isOn('/labs') ? 'on' : ''}>Labs</Link>
          <Link to="/trainee" className="">Leaderboard</Link>
        </nav>
        <div className="fs-right">
          <span className="fs-pts">{pts.toLocaleString()} <small>pts</small></span>
          <Link to="/trainee" className="fs-av" title={name}>{initials(name)}</Link>
        </div>
      </header>
      <main id="main" tabIndex={-1} className="fs-main">{children}</main>
    </div>
  );
}
